// Workspace-level routes: business profile + settings, team, allowed domains, marketing
// configuration, widget installation key, and the Frontend-QA contract endpoints.
import {
  usersStore, tenantsStore, sessionsStore, profilesStore, domainsStore, marketingStore, settingsStore,
  tenantKeysStore, teamMemberView, tenantName
} from "../core";
import { ROLE_PERMISSIONS, ALL_PERMISSIONS, INVOICES, BREAKPOINTS, TEAM_ROLES, TEAM_ROLE_TO_ROLE } from "../fixtures";
import { overviewItem, modules } from "../../../integrationQA/config/navigation";
import {
  ok, fail, validationFail, genId, randomCode, DOMAIN_RE, nowIso
} from "../helpers";
import {
  collect, emailError, firstMessage, lengthError, nameError, oneOfError, targetError, tenDigitPhoneError, urlError
} from "../validate";

const pendingAccessRequests = new Set();
const MARKETING_SECTIONS = ["greeting", "cta", "action", "rules", "conversions", "integrations"];

function dropSessionsFor(userId) {
  const sessions = sessionsStore.all();
  Object.keys(sessions).forEach((token) => { if (sessions[token].userId === userId) delete sessions[token]; });
  sessionsStore.save(sessions);
}

const SAFE_EVENT = /^[A-Za-z0-9_-]+$/;

/** Light schema checks for the per-workspace marketing documents. Returns a fields map or null. */
function validateMarketingDoc(section, doc) {
  if (section === "greeting") {
    return collect([
      ["greetingName", lengthError(doc.greetingName, { min: 2, max: 60, label: "Greeting name" })],
      ["message", lengthError(doc.message, { min: 5, max: 300, label: "Greeting message" })],
      ["pageRule", lengthError(doc.pageRule, { min: 1, max: 200, label: "Page rule" })],
      ["cta", lengthError(doc.cta, { max: 40, label: "CTA text" })]
    ]);
  }
  if (section === "cta") {
    return collect([
      ["ctaName", lengthError(doc.ctaName, { min: 2, max: 60, label: "CTA name" })],
      ["buttonLabel", lengthError(doc.buttonLabel, { min: 2, max: 30, label: "Button label" })],
      ["destination", targetError(doc.destination)],
      doc.actionType !== undefined ? ["actionType", oneOfError(doc.actionType, ["URL", "Registration Page", "Booking Page", "Demo Form", "Lead Form"], "Action type")] : null
    ].filter(Boolean));
  }
  if (section === "action") {
    return collect([
      ["action", oneOfError(doc.action, ["URL", "FORM", "CHAT", "EVENT"], "Action")],
      doc.action === "URL" ? ["url", urlError(doc.url, { required: true, label: "Destination URL" })] : null,
      doc.action === "EVENT" ? ["eventName", !SAFE_EVENT.test(String(doc.eventName || "").trim()) ? "Event name may contain only letters, numbers, underscores and hyphens." : ""] : null
    ].filter(Boolean));
  }
  if (section === "rules" || section === "conversions") {
    if (!Array.isArray(doc.rows) || doc.rows.length > 100) return { rows: "Rows must be a list of at most 100 items." };
    const bad = doc.rows.find((r) => lengthError(r && r.name, { min: 2, max: 80, label: "Name" }));
    if (bad !== undefined) return { rows: lengthError(bad && bad.name, { min: 2, max: 80, label: "Name" }) };
    if (section === "rules") {
      const noCondition = doc.rows.find((r) => lengthError(r.condition, { min: 1, max: 200, label: "Condition" }));
      if (noCondition) return { rows: lengthError(noCondition.condition, { min: 1, max: 200, label: "Condition" }) };
    } else {
      const badEvent = doc.rows.find((r) => !SAFE_EVENT.test(String(r.event || "").trim()));
      if (badEvent) return { rows: "Event name is required and may contain only letters, numbers, underscores and hyphens." };
    }
    return null;
  }
  if (section === "integrations") {
    const values = Object.values(doc.statuses || {});
    return values.every((v) => v === "Connected" || v === "Available") ? null : { statuses: "Integration status must be Connected or Available." };
  }
  return null;
}

export const workspaceRoutes = [
  // ---- Business profile + settings (FR-WORK-001/002) -------------------------------------------
  {
    method: "GET", pattern: /^\/api\/business-profile$/, permission: "business:manage",
    handler: ({ tenantId, scenario }) => ok(200, scenario === "empty" ? null : (profilesStore.all()[tenantId] || {}))
  },
  {
    method: "PATCH", pattern: /^\/api\/business-profile$/, permission: "business:manage",
    handler: ({ tenantId, body }) => {
      const has = (key) => Object.prototype.hasOwnProperty.call(body, key);
      const TIMEZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York"];
      const LANGS = ["English", "Hindi", "Telugu", "Tamil", "Spanish", "French"];
      const place = (key, label) => (has(key) ? lengthError(body[key], { max: 60, label }) || (/[<>{}]/.test(String(body[key])) ? `${label} contains characters that aren't allowed.` : "") : "");
      // Business Profile screen sends every field; Business Settings sends only domain/timezone/language.
      const fields = collect([
        has("businessName") && ["businessName", nameError(body.businessName, { label: "Business name", max: 100 })],
        has("industry") && ["industry", lengthError(body.industry, { max: 60, label: "Industry" })],
        has("website") && ["website", urlError(body.website, { label: "Website URL" })],
        has("phone") && ["phone", tenDigitPhoneError(body.phone)],
        has("address") && ["address", lengthError(body.address, { max: 200, label: "Address" })],
        ["city", place("city", "City")], ["state", place("state", "State")], ["country", place("country", "Country")],
        has("description") && ["description", lengthError(body.description, { max: 500, label: "Description" })],
        has("domain") && ["domain", !String(body.domain || "").trim() ? "Business domain is required." : (DOMAIN_RE.test(String(body.domain).trim()) ? "" : "Enter a valid domain, e.g. example.com.")],
        has("timezone") && ["timezone", body.timezone ? oneOfError(body.timezone, TIMEZONES, "Timezone") : "Timezone is required."],
        has("language") && ["language", body.language ? oneOfError(body.language, LANGS, "Language") : "Language is required."]
      ].filter(Boolean));
      if (fields) return validationFail(fields, firstMessage(fields));
      const profiles = profilesStore.all();
      profiles[tenantId] = { ...(profiles[tenantId] || {}), ...body };
      profilesStore.save(profiles);
      if (has("businessName")) {
        tenantsStore.save(tenantsStore.all().map((t) => (t.id === tenantId ? { ...t, name: String(body.businessName).trim() } : t)));
      }
      return ok(200, profiles[tenantId]);
    }
  },
  {
    method: "GET", pattern: /^\/api\/settings$/, permission: "business:manage",
    handler: ({ tenantId }) => ok(200, {
      workspace: tenantName(tenantId), timezone: "Asia/Kolkata", emailAlerts: true, maintenance: false,
      ...(settingsStore.all()[tenantId] || {})
    })
  },
  {
    method: "PUT", pattern: /^\/api\/settings$/, permission: "business:manage",
    handler: ({ tenantId, body }) => {
      const fields = collect([
        ["workspace", nameError(body.workspace, { label: "Workspace name", max: 80 })],
        ["timezone", oneOfError(body.timezone, ["Asia/Kolkata", "UTC", "America/New_York", "Europe/London"], "Timezone")]
      ]);
      if (fields) return validationFail(fields, firstMessage(fields));
      const all = settingsStore.all();
      all[tenantId] = { workspace: String(body.workspace).trim(), timezone: body.timezone, emailAlerts: !!body.emailAlerts, maintenance: !!body.maintenance };
      settingsStore.save(all);
      return ok(200, all[tenantId]);
    }
  },

  // ---- Team (users + invites + roles) ------------------------------------------------------------
  {
    method: "GET", pattern: /^\/api\/team\/members$/, permission: "users:manage",
    handler: ({ tenantId, scenario }) => {
      const items = scenario === "empty" ? [] : usersStore.all().filter((u) => u.tenantId === tenantId).map((u) => teamMemberView(u, { includeInvite: true }));
      return ok(200, items, { total: items.length, tenantId });
    }
  },
  {
    method: "POST", pattern: /^\/api\/team\/members$/, permission: "users:manage",
    handler: ({ tenantId, body }) => {
      const email = String(body.email || "").trim().toLowerCase();
      if (emailError(email)) return validationFail({ email: emailError(email) }, emailError(email));
      if (!["Admin", "Editor", "Viewer"].includes(body.role)) return validationFail({ role: "Please select a valid role." }, "Please select a valid role.");
      const users = usersStore.all();
      if (users.some((u) => u.email.toLowerCase() === email)) {
        return fail(409, "EMAIL_EXISTS", "A user with this email already exists.", { fields: { email: "A user with this email already exists." } });
      }
      const member = {
        id: genId("u"), tenantId, name: email.split("@")[0], email, password: null, teamRole: body.role,
        status: "Invited", verified: false, inviteCode: randomCode(), lastActive: "—"
      };
      usersStore.save([...users, member]);
      // `inviteCode` is what a real backend would email; the Team screen shows it so the invite can be accepted.
      return ok(201, teamMemberView(member, { includeInvite: true }));
    }
  },
  {
    method: "PATCH", pattern: /^\/api\/team\/members\/([\w-]+)$/, permission: "users:manage",
    handler: ({ tenantId, match, body }) => {
      const users = usersStore.all();
      const member = users.find((u) => u.id === match[1] && u.tenantId === tenantId);
      if (!member) return fail(404, "NOT_FOUND", "Team member not found.");
      if (member.teamRole === "Owner") return fail(409, "OWNER_PROTECTED", "The workspace Owner's role can't be changed.");
      if (!TEAM_ROLES.includes(body.role) || body.role === "Owner" || !TEAM_ROLE_TO_ROLE[body.role]) {
        return validationFail({ role: "Please select a valid role." }, "Please select a valid role.");
      }
      member.teamRole = body.role;
      usersStore.save(users);
      return ok(200, teamMemberView(member, { includeInvite: true }));
    }
  },
  {
    method: "POST", pattern: /^\/api\/team\/members\/([\w-]+)\/status$/, permission: "users:manage",
    handler: ({ tenantId, userId, match, body }) => {
      if (!["Active", "Deactivated"].includes(body.status)) return validationFail({ status: "Choose a valid user status." }, "Choose a valid user status.");
      const users = usersStore.all();
      const member = users.find((u) => u.id === match[1] && u.tenantId === tenantId);
      if (!member) return fail(404, "NOT_FOUND", "Team member not found.");
      if (member.teamRole === "Owner") return fail(409, "OWNER_PROTECTED", "The workspace Owner can't be deactivated.");
      if (member.id === userId) return fail(409, "SELF_DEACTIVATION", "You can't change your own status here. Use Deactivate Account instead.");
      if (member.status === "Invited" && body.status === "Active") return fail(409, "NOT_ACCEPTED", "This person hasn't accepted their invitation yet.");
      // Reactivating someone who never accepted their invite puts them back to "Invited", not "Active".
      member.status = body.status === "Active" && !member.password ? "Invited" : body.status;
      usersStore.save(users);
      if (body.status === "Deactivated") dropSessionsFor(member.id);
      return ok(200, { ...teamMemberView(member), result: "success" });
    }
  },

  // ---- Allowed domains (FR-WORK-005) -------------------------------------------------------------
  {
    method: "GET", pattern: /^\/api\/allowed-domains$/, permission: "business:manage",
    handler: ({ tenantId, scenario }) => {
      const items = scenario === "empty" ? [] : (domainsStore.all()[tenantId] || []);
      return ok(200, items, { total: items.length, tenantId });
    }
  },
  {
    method: "POST", pattern: /^\/api\/allowed-domains$/, permission: "business:manage",
    handler: ({ tenantId, body }) => {
      const domain = String(body.domain || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
      if (!DOMAIN_RE.test(domain)) return validationFail({ domain: "Enter a valid hostname, for example app.yourcompany.com." }, "Enter a valid hostname, for example app.yourcompany.com.");
      const all = domainsStore.all();
      const existing = all[tenantId] || [];
      if (existing.some((d) => d.domain === domain)) return fail(409, "DOMAIN_EXISTS", "This domain is already on the allow-list.", { fields: { domain: "This domain is already on the allow-list." } });
      const entry = { id: genId("dom"), domain, addedAt: nowIso() };
      all[tenantId] = [...existing, entry];
      domainsStore.save(all);
      return ok(201, entry);
    }
  },
  {
    method: "DELETE", pattern: /^\/api\/allowed-domains\/([\w-]+)$/, permission: "business:manage",
    handler: ({ tenantId, match }) => {
      const all = domainsStore.all();
      const existing = all[tenantId] || [];
      if (!existing.some((d) => d.id === match[1])) return fail(404, "NOT_FOUND", "Domain not found.");
      all[tenantId] = existing.filter((d) => d.id !== match[1]);
      domainsStore.save(all);
      return ok(200, { id: match[1] });
    }
  },

  // ---- Marketing configuration documents (greeting, CTA, page rules, conversions, integrations) --
  {
    method: "GET", pattern: /^\/api\/marketing\/([\w-]+)$/, permission: "marketing:manage",
    handler: ({ tenantId, match }) => {
      if (!MARKETING_SECTIONS.includes(match[1])) return fail(404, "NOT_FOUND", "Unknown marketing section.");
      const entry = (marketingStore.all()[tenantId] || {})[match[1]];
      return ok(200, { section: match[1], doc: entry ? entry.doc : null, updatedAt: entry ? entry.updatedAt : null });
    }
  },
  {
    method: "PUT", pattern: /^\/api\/marketing\/([\w-]+)$/, permission: "marketing:manage",
    handler: ({ tenantId, match, body }) => {
      if (!MARKETING_SECTIONS.includes(match[1])) return fail(404, "NOT_FOUND", "Unknown marketing section.");
      if (body.doc === undefined || body.doc === null || typeof body.doc !== "object") return validationFail({ doc: "A document is required." });
      const docFields = validateMarketingDoc(match[1], body.doc);
      if (docFields) return validationFail(docFields, firstMessage(docFields));
      const all = marketingStore.all();
      all[tenantId] = { ...(all[tenantId] || {}), [match[1]]: { doc: body.doc, updatedAt: nowIso() } };
      marketingStore.save(all);
      return ok(200, { section: match[1], doc: body.doc, updatedAt: all[tenantId][match[1]].updatedAt });
    }
  },

  // ---- Widget installation key -------------------------------------------------------------------
  {
    method: "GET", pattern: /^\/api\/widget-installation-key$/, permission: "marketing:manage",
    handler: ({ tenantId }) => {
      const keys = tenantKeysStore.all();
      if (!keys[tenantId]) {
        keys[tenantId] = `cb_demo_${tenantId.replace(/^tenant-/, "").replace(/[^a-z0-9]/gi, "").slice(0, 10)}_${randomCode()}`;
        tenantKeysStore.save(keys);
      }
      // isDemo stays true in the mock: the backend must provision real per-workspace keys.
      return ok(200, { key: keys[tenantId], isDemo: true, status: "success" });
    }
  },

  // ---- Frontend QA contract endpoints --------------------------------------------------------------
  {
    method: "GET", pattern: /^\/api\/billing\/invoices$/, permission: "billing:read",
    handler: ({ tenantId, scenario }) => {
      const items = scenario === "empty" ? [] : INVOICES.filter((i) => i.tenantId === tenantId);
      return ok(200, items, { total: items.length, tenantId });
    }
  },
  {
    method: "GET", pattern: /^\/api\/users$/, permission: "users:manage",
    handler: ({ tenantId, scenario }) => {
      const items = scenario === "empty" ? [] : usersStore.all().filter((u) => u.tenantId === tenantId)
        .map((u) => ({ id: u.id, tenantId: u.tenantId, name: u.name, role: u.teamRole }));
      return ok(200, items, { total: items.length, tenantId });
    }
  },
  {
    method: "GET", pattern: /^\/api\/tenant\/context$/, permission: null,
    handler: ({ tenantId, role, scenario }) => {
      if (scenario === "empty") return ok(200, null);
      const tenant = tenantsStore.all().find((t) => t.id === tenantId);
      return ok(200, { ...tenant, role, permissions: ROLE_PERMISSIONS[role] || [] });
    }
  },
  {
    method: "POST", pattern: /^\/api\/access-requests$/, permission: null,
    handler: ({ tenantId, role, body }) => {
      const fields = {};
      if (!ALL_PERMISSIONS.includes(body.permission)) fields.permission = "Choose a valid permission.";
      if (!body.reason || body.reason.trim().length < 10) fields.reason = "Please explain why you need access (minimum 10 characters).";
      if (Object.keys(fields).length) return validationFail(fields);
      const key = `${tenantId}:${role}:${body.permission}`;
      if (pendingAccessRequests.has(key)) return fail(409, "ACCESS_REQUEST_EXISTS", "This record already exists.");
      pendingAccessRequests.add(key);
      return ok(201, { id: `ar_${pendingAccessRequests.size}`, permission: body.permission, status: "pending" });
    }
  },
  {
    method: "GET", pattern: /^\/api\/qa\/breakpoints$/, permission: null, tenant: false,
    handler: ({ scenario }) => ok(200, scenario === "empty" ? [] : BREAKPOINTS)
  },
  {
    method: "GET", pattern: /^\/api\/qa\/navigation$/, permission: null, tenant: false,
    handler: ({ scenario }) => {
      const items = scenario === "empty" ? [] : [overviewItem, ...modules].map((m) => ({ path: m.path, label: m.label, icon: m.icon, requires: null }));
      return ok(200, items, { total: items.length });
    }
  },
  {
    method: "POST", pattern: /^\/api\/qa\/audit-reports$/, permission: null, tenant: false,
    handler: ({ body }) => {
      const fields = {};
      if (!body.reviewer || body.reviewer.trim().length < 2) fields.reviewer = "Reviewer name is required (minimum 2 characters).";
      if (!Array.isArray(body.results) || body.results.length === 0) fields.results = "Run the audit before submitting a report.";
      if (Object.keys(fields).length) return validationFail(fields);
      return ok(201, { reportId: genId("rep"), savedAt: nowIso() });
    }
  }
];
